import { Link } from 'react-router-dom';
import { Package, ExternalLink, ShoppingCart, ThumbsUp, ChevronRight } from 'lucide-react';
import type { ProductRelation, RelationType } from '../../types/products';
import { RELATION_TYPE_LABELS } from '../../types/products';
import { formatPrice } from '../../services/productService';

interface ProductRelationsProps {
  relations: ProductRelation[];
  type: RelationType;
  variant?: 'grid' | 'list';
}

const relationIcons: Record<RelationType, typeof ShoppingCart> = {
  frequently_bought_together: ShoppingCart,
  recommended: ThumbsUp,
};

export function ProductRelations({ relations, type, variant = 'grid' }: ProductRelationsProps) {
  const filteredRelations = relations.filter((r) => r.relation_type === type);

  if (filteredRelations.length === 0) {
    return null;
  }

  const Icon = relationIcons[type];

  // List variant for sidebar
  if (variant === 'list') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Icon className="w-4 h-4 text-primary" />
          <h4 className="font-semibold text-sm text-[#1A1A1A] dark:text-white">{RELATION_TYPE_LABELS[type]}</h4>
        </div>
        <div className="space-y-2">
          {filteredRelations.map((relation) => {
            if (relation.related_product) {
              return (
                <Link
                  key={relation.id}
                  to={`/products/${relation.related_product.slug}`}
                  className="group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A] dark:text-white truncate group-hover:text-primary transition-colors">
                      {relation.related_product.name}
                    </p>
                    {relation.related_product.price_gross && (
                      <p className="text-xs font-semibold text-[#6B6B6B] dark:text-slate-400">
                        {formatPrice(relation.related_product.price_gross, relation.related_product.currency)}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-500 group-hover:text-primary transition-colors" />
                </Link>
              );
            }

            if (relation.related_external_url) {
              return (
                <a
                  key={relation.id}
                  href={relation.related_external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ExternalLink className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                  </div>
                  <span className="text-sm text-[#6B6B6B] dark:text-slate-400">Externer Link</span>
                </a>
              );
            }

            return null;
          })}
        </div>
      </div>
    );
  }

  // Grid variant (default)
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-slate-700">
        <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="font-semibold text-[#1A1A1A] dark:text-white">{RELATION_TYPE_LABELS[type]}</h3>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filteredRelations.map((relation) => {
            if (relation.related_product) {
              return (
                <Link
                  key={relation.id}
                  to={`/products/${relation.related_product.slug}`}
                  className="group bg-gray-50 dark:bg-slate-700 rounded-xl p-3 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                  <div className="aspect-square bg-white dark:bg-slate-600 rounded-lg mb-2 flex items-center justify-center">
                    <Package className="w-8 h-8 text-gray-300 dark:text-slate-500" />
                  </div>
                  <p className="text-sm font-medium text-[#1A1A1A] dark:text-white line-clamp-2 group-hover:text-primary transition-colors">
                    {relation.related_product.name}
                  </p>
                  {relation.related_product.price_gross && (
                    <p className="text-sm font-bold text-[#1A1A1A] dark:text-white mt-1">
                      {formatPrice(relation.related_product.price_gross, relation.related_product.currency)}
                    </p>
                  )}
                </Link>
              );
            }

            if (relation.related_external_url) {
              return (
                <a
                  key={relation.id}
                  href={relation.related_external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4 text-[#6B6B6B] dark:text-slate-400" />
                  <span className="text-sm text-[#6B6B6B] dark:text-slate-400">Externer Link</span>
                </a>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
}
